import React from "react";
import PropTypes from "prop-types";

const euro = (n) => `€${Math.round(n || 0).toLocaleString()}`;

// SourcePriorityPicker renders the priority order of cost sources as a movable list, with the
// source being defined/edited shown as a highlighted "This source" row. Higher in the list =
// claims cost first. The user moves the "This source" row up/down; the parent derives which
// existing sources rank ahead (everything above the row) to drive the live residual preview
// and the saved order.
//
// `items` is the full ordered list, each: { key, label, cost, isSelf }. Exactly one isSelf.
// onMove(direction) shifts the self row by -1 (up) or +1 (down); the parent clamps + reorders.
const SourcePriorityPicker = ({ items, onMove }) => {
  return (
    <ol className="space-y-1" aria-label="Source priority order">
      {items.map((it, i) => {
        const rank = i + 1;
        if (it.isSelf) {
          return (
            <li
              key={it.key}
              className="flex items-center gap-2 rounded-ctrl border border-primary bg-primary-soft px-3 py-2"
              aria-current="true"
            >
              <span className="text-[11px] tabular-nums text-text-2 w-5">{rank}</span>
              <span className="flex-1 text-sm font-medium text-text">{it.label || "This source"}</span>
              <span className="text-[11px] text-text-2">claims at this rank</span>
              <span className="flex flex-col leading-none">
                <button
                  type="button"
                  onClick={() => onMove(-1)}
                  disabled={i === 0}
                  aria-label="Move this source up (claim earlier)"
                  className="text-text-2 hover:text-text disabled:opacity-30 disabled:cursor-not-allowed text-xs"
                >
                  ▲
                </button>
                <button
                  type="button"
                  onClick={() => onMove(1)}
                  disabled={i === items.length - 1}
                  aria-label="Move this source down (claim later)"
                  className="text-text-2 hover:text-text disabled:opacity-30 disabled:cursor-not-allowed text-xs"
                >
                  ▼
                </button>
              </span>
            </li>
          );
        }
        return (
          <li key={it.key} className="flex items-center gap-2 px-3 py-1.5 text-text-2">
            <span className="text-[11px] tabular-nums text-text-3 w-5">{rank}</span>
            <span className="flex-1 text-sm truncate">{it.label}</span>
            {it.cost != null && <span className="text-[11px] tabular-nums text-text-2">{euro(it.cost)}/mo</span>}
          </li>
        );
      })}
      <li className="px-3 pt-1 text-[11px] text-text-2">
        Sources higher in this list claim matching cost first. Move “This source” up to capture
        more, down to leave it for others.
      </li>
    </ol>
  );
};

SourcePriorityPicker.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string,
      cost: PropTypes.number,
      isSelf: PropTypes.bool,
    })
  ).isRequired,
  onMove: PropTypes.func.isRequired,
};

export default SourcePriorityPicker;
